import { useEffect, useMemo, useState } from 'react'

import { useQuery } from '@apollo/client'
import { Box, Skeleton, Text } from '@factorialco/deprecated-design-system'
import { F0Alert } from '@factorialco/f0-react'
import { EntityId, EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'
import { InfoCircle } from '@factorialco/f0-react/icons/app'

import { TrainingsWithClassesDocument } from 'generated/resources/graphql'

import { EntitySelectWrapper } from 'modules/core/components/EntitySelectWrapper'
import { flatSelection } from 'modules/core/components/EntitySelectWrapper/lib/flat'
import i18n from 'modules/core/lib/i18n'

import { useExchangeRate } from 'modules/expenses/hooks/useExchangeRate'

import { BudgetSummaryCards } from './AllocationStep/BudgetSummaryCards'
import {
  calculateConvertedAllocationCents,
  getSelectedEntities,
  hasForeignCurrency,
  mapTrainingsToEntities,
} from './AllocationStep/lib/trainingEntityMapper'

interface AllocationFieldValue {
  selectedTrainingClassIds: string[]
  selectedTrainingIds: string[]
}

interface AllocationFieldProps {
  value: AllocationFieldValue
  onChange: (value: AllocationFieldValue) => void
  currency: string
  budgetAmountCents: number
}

const AllocationField = ({
  value,
  onChange,
  currency,
  budgetAmountCents,
}: AllocationFieldProps) => {
  const [expandedElements, setExpandedElements] = useState<EntityId[]>([])
  const [exchangeRate, setExchangeRate] = useState<number>(1)

  const { data: trainingsData, loading } = useQuery(TrainingsWithClassesDocument, {})
  const trainings = useMemo(() => {
    const allTrainings = (trainingsData?.trainings?.trainingsConnection?.nodes ?? []).filter(
      (node): node is NonNullable<typeof node> => node !== null
    )

    return allTrainings
      .filter((training) => training._policies?.edit?.can)
      .map((training) => ({
        ...training,
        trainingClassesConnection: {
          ...training.trainingClassesConnection,
          nodes: training.trainingClassesConnection.nodes.filter((cls) => !cls.budgetMovement),
        },
      }))
      .filter((training) => training.trainingClassesConnection.nodes.length > 0)
  }, [trainingsData])

  const needsConversion = useMemo(
    () => hasForeignCurrency(trainings, currency),
    [trainings, currency]
  )

  const { fetch: fetchExchangeRate, loading: loadingRate } = useExchangeRate({
    onCompleted: ({ rate }) => setExchangeRate(rate),
  })

  useEffect(() => {
    if (!trainings.length || !needsConversion) return
    const firstClass = trainings[0]?.trainingClassesConnection.nodes[0]
    if (!firstClass) return
    fetchExchangeRate({
      baseCurrency: firstClass.currency,
      targetCurrency: currency,
      date: new Date().toISOString().split('T')[0],
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trainings, needsConversion, currency])

  const entities = useMemo(
    () => mapTrainingsToEntities(trainings, expandedElements),
    [trainings, expandedElements]
  )

  const selectedEntities = useMemo(
    () => getSelectedEntities(entities, value.selectedTrainingClassIds),
    [entities, value.selectedTrainingClassIds]
  )

  const totalBudgetCents = useMemo(() => {
    return Math.round(budgetAmountCents * 100)
  }, [budgetAmountCents])

  const allocationCents = useMemo(
    () =>
      calculateConvertedAllocationCents(trainings, value.selectedTrainingClassIds, exchangeRate),
    [trainings, value.selectedTrainingClassIds, exchangeRate]
  )

  const availableCents = totalBudgetCents - allocationCents

  const handleSelectMultiple = (selection: EntitySelectEntity[] | null) => {
    if (!selection) {
      onChange({
        selectedTrainingClassIds: [],
        selectedTrainingIds: [],
      })
      return
    }

    const classIds = flatSelection(selection).map((item) => String(item.subId))
    const trainingIds = selection.map((entity) => String(entity.id))

    onChange({
      selectedTrainingClassIds: classIds,
      selectedTrainingIds: trainingIds,
    })
  }

  return (
    <Box gap="s24" flexDirection="column">
      <BudgetSummaryCards
        totalBudgetCents={totalBudgetCents}
        allocationCents={allocationCents}
        availableCents={availableCents}
        currency={currency}
        loading={loadingRate}
      />

      <Box gap="s8" flexDirection="column" maxWidth="s600">
        {loading ? (
          <Box flexDirection="column" gap="s8">
            <Skeleton height="s64" width="full" />
            <Skeleton height="s64" width="full" />
          </Box>
        ) : trainings.length === 0 ? (
          <Box padding="s24" alignItems="center">
            <Text.Normal color="grey800">
              {i18n.t('budgets.forms.trainings_budget.allocation.no_trainings')}
            </Text.Normal>
          </Box>
        ) : (
          <EntitySelectWrapper
            entities={entities}
            groups={[{ label: i18n.t('common.all'), value: 'all' }]}
            expandedElements={expandedElements}
            selectedGroup="all"
            selectedGroupedEntities={selectedEntities}
            loading={loading}
            multiple
            hiddenAvatar
            singleSelector={false}
            label={i18n.t('budgets.forms.trainings_budget.allocation.training_group')}
            placeholder={i18n.t('budgets.forms.trainings_budget.allocation.placeholder')}
            searchPlaceholder={i18n.t('common.search')}
            selectAllLabel={i18n.t('common.select_all')}
            clearLabel={i18n.t('common.clear')}
            selectedLabel={i18n.t('common.selected')}
            selectedItemsCopy={i18n.t(
              'budgets.forms.trainings_budget.allocation.trainings_selected'
            )}
            notFoundTitle={i18n.t('budgets.forms.trainings_budget.allocation.no_trainings')}
            notFoundSubtitle=""
            width="s600"
            onSelect={() => {}}
            onSelectMultiple={handleSelectMultiple}
            onGroupChange={() => {}}
            onSelectExpandedElements={setExpandedElements}
            size="md"
            required={false}
          />
        )}
      </Box>

      {needsConversion && exchangeRate !== 1 && (
        <Box maxWidth="s600">
          <F0Alert
            variant="info"
            icon={InfoCircle}
            title={i18n.t('budgets.forms.trainings_budget.allocation.conversion_info_title')}
            description={i18n.t(
              'budgets.forms.trainings_budget.allocation.conversion_info_description'
            )}
          />
        </Box>
      )}

      <Box maxWidth="s600">
        <F0Alert
          variant="neutral"
          icon={InfoCircle}
          title={i18n.t('budgets.forms.trainings_budget.allocation.info_title')}
          description={i18n.t('budgets.forms.trainings_budget.allocation.info_description')}
        />
      </Box>
    </Box>
  )
}

export default AllocationField
